import FilterPanel from 'components/filter/FilterPanel';
import StyledDateInput from 'components/styledInput/StyledDateInput';
import StyledInput from 'components/styledInput/StyledInput';
import { TASK_TYPES, taskTypeLabel } from 'constants/taskTypes';
import { fr } from 'date-fns/locale';
import { dayLabel, fromIsoDay, isoDay } from 'helper/timeLabels';
import useText from 'hooks/useText';
import DatePicker, { registerLocale } from 'react-datepicker';

registerLocale('fr-FR', fr);

const DUE_LABELS = {
  late: 'dueLate',
  today: 'dueToday',
  tomorrow: 'dueTomorrow',
  week: 'dueWeek',
  next: 'dueNext',
  later: 'dueLater',
};

/**
 * The filters of the tasks in the admin's « Plus de filtres » panel: the due
 * period (or a given day) and the types. `counts(key, id)` says how many
 * tasks of the tab each choice keeps.
 */
const TaskFilterPanel = ({ isOpen, onClose, filters, onChange, counts }) => {
  const { language, text } = useText('tasks');
  const { due, day, types } = filters;
  const typeLabel = (type) => taskTypeLabel(language, type);

  const renderFilterByKey = (key) =>
    key === 'due' ? (
      <div className="flex flex-col gap-3">
        {Object.entries(DUE_LABELS).map(([id, label]) => (
          <StyledInput
            key={id}
            type="radio"
            name="task-due"
            label={text(label)}
            detail={String(counts('due', id))}
            checked={due === id}
            onChange={() => onChange({ due: id })}
          />
        ))}
        <DatePicker
          dateFormat="dd/MM/yyyy"
          selected={due === 'day' && day ? fromIsoDay(day) : null}
          onChange={(date) => date && onChange({ due: 'day', day: isoDay(date) })}
          locale="fr-FR"
          popperPlacement="bottom"
          customInput={<StyledDateInput label={text('filterDay')} />}
        />
      </div>
    ) : (
      <div className="flex flex-col gap-3">
        {Object.keys(TASK_TYPES).map((type) => (
          <StyledInput
            key={type}
            type="checkbox"
            label={typeLabel(type)}
            detail={String(counts('types', type))}
            checked={types.includes(type)}
            onChange={() =>
              onChange({ types: types.includes(type) ? types.filter((item) => item !== type) : [...types, type] })
            }
          />
        ))}
      </div>
    );

  const displayValue = (key) => {
    if (key === 'due') {
      if (due === 'all') return null;
      return due === 'day' ? dayLabel(fromIsoDay(day)) : text(DUE_LABELS[due]);
    }
    return types.length ? types.map(typeLabel).join(', ') : null;
  };

  return (
    <FilterPanel
      isOpen={isOpen}
      onClose={onClose}
      activeFilterCount={(due !== 'all') + (types.length > 0)}
      filters={[
        { key: 'due', label: text('filterDue') },
        { key: 'types', label: text('filterTypes') },
      ]}
      renderFilterByKey={renderFilterByKey}
      getFilterDisplayValue={displayValue}
      clearFilterByKey={(key) => onChange(key === 'due' ? { due: 'all', day: null } : { types: [] })}
      onReset={() => onChange({ due: 'all', day: null, types: [] })}
      onApply={onClose}
    />
  );
};

export default TaskFilterPanel;
